import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Divider,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalculateIcon from '@mui/icons-material/Calculate';
import { HOUSE_TYPES, COST_RATES } from '../../utils/costRates';

const StepReview = ({
  data,
  onChange,
  onCalculate,
  onBack,
  isCalculating = false,
  language = 'en'
}) => {
  const content = {
    en: {
      stepLabel: 'Step 3 of 3',
      title: 'Review Your Inputs',
      houseType: 'House Type',
      builtUpArea: 'Built-Up Area',
      floors: 'Number of Floors',
      finishing: 'Finishing Level',
      projectName: 'Project Name (optional)',
      projectNamePlaceholder: 'My Dream Home',
      calculateBtn: 'Generate Estimation',
      calculating: 'Calculating...',
      backBtn: 'Back',
      sqft: 'sq ft',
      floor: 'floor',
      floorsLabel: 'floors'
    },
    my: {
      stepLabel: 'Langkah 3 daripada 3',
      title: 'Semak Maklumat Anda',
      houseType: 'Jenis Rumah',
      builtUpArea: 'Luas Binaan',
      floors: 'Bilangan Tingkat',
      finishing: 'Tahap Kemasan',
      projectName: 'Nama Projek (pilihan)',
      projectNamePlaceholder: 'Rumah Impian Saya',
      calculateBtn: 'Jana Anggaran',
      calculating: 'Mengira...',
      backBtn: 'Kembali',
      sqft: 'kaki persegi',
      floor: 'tingkat',
      floorsLabel: 'tingkat'
    }
  };

  const t = content[language] || content.en;

  const houseTypeConfig = HOUSE_TYPES[data.houseType];
  const finishingConfig = COST_RATES[data.finishingLevel];

  const handleProjectNameChange = (event) => {
    onChange({ ...data, projectName: event.target.value });
  };

  const reviewItems = [
    {
      label: t.houseType,
      value: language === 'my' ? houseTypeConfig?.labelMy : houseTypeConfig?.label
    },
    {
      label: t.builtUpArea,
      value: `${data.builtUpArea?.toLocaleString()} ${t.sqft}`
    },
    {
      label: t.floors,
      value: `${data.floors} ${data.floors === 1 ? t.floor : t.floorsLabel}`
    },
    {
      label: t.finishing,
      value: language === 'my' ? finishingConfig?.labelMy : finishingConfig?.label
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: 'auto' }}>
      {/* Progress indicator */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t.stepLabel}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'primary.main' }} />
          <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'primary.main' }} />
          <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'primary.main' }} />
        </Box>
      </Box>

      {/* Title */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 4 }}>
        {t.title}
      </Typography>

      {/* Review Card */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
          mb: 4
        }}
      >
        {reviewItems.map((item, index) => (
          <React.Fragment key={item.label}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 3,
                py: 2.5
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {item.label}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {item.value}
              </Typography>
            </Box>
            {index < reviewItems.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Paper>

      {/* Project Name */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
          {t.projectName}
        </Typography>
        <TextField
          fullWidth
          value={data.projectName || ''}
          onChange={handleProjectNameChange}
          placeholder={t.projectNamePlaceholder}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2
            }
          }}
        />
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 5 }}>
        <Button
          variant="contained"
          onClick={onCalculate}
          disabled={isCalculating}
          size="large"
          startIcon={
            isCalculating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CalculateIcon />
            )
          }
          sx={{
            py: 2,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1.1rem',
            boxShadow: '0 4px 14px rgba(30, 58, 95, 0.2)',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(30, 58, 95, 0.25)'
            }
          }}
        >
          {isCalculating ? t.calculating : t.calculateBtn}
        </Button>

        <Button
          variant="text"
          onClick={onBack}
          disabled={isCalculating}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none' }}
        >
          {t.backBtn}
        </Button>
      </Box>
    </Box>
  );
};

export default StepReview;
